import { XIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect } from "react";
import { useIntl } from "react-intl";

import { Button, type ButtonProps } from "@/components/Button";
import { useDrawerContext } from "@/components/Drawer/DrawerContext";
import { IconButton } from "@/components/IconButton/IconButton";

type DrawerTopBarProps = {
  onCloseClick: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actionElement?: React.ReactNode;
};

export function DrawerTopBar({
  onCloseClick,
  title,
  subtitle,
  actionElement,
}: DrawerTopBarProps) {
  const intl = useIntl();
  const {
    isScrolled,
    isSwipeable,
    backgroundColor,
    onTopBarMount,
    onTopBarUnmount,
  } = useDrawerContext();

  useEffect(() => {
    onTopBarMount?.();
    return onTopBarUnmount;
  }, [onTopBarMount, onTopBarUnmount]);

  return (
    <div
      className={clsx(
        "sticky top-0 left-0 z-50 w-full border-b-stroke-neutral-soft px-main pt-2 backdrop-blur-xs",
        {
          "border-b": isScrolled,
          "bg-white/70": backgroundColor === "white",
          "bg-surface-background/70": backgroundColor === "background",
        },
      )}
    >
      {isSwipeable ? (
        // puller
        <div className="mx-auto h-1 w-10 shrink-0 rounded-full bg-gray-300" />
      ) : null}

      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 pt-4 pb-2">
        <div className="justify-self-start">{actionElement}</div>

        <div className="justify-self-center overflow-hidden text-center text-base font-medium text-ellipsis whitespace-nowrap">
          {title}
          {subtitle ? (
            <div className="mt-1 text-sm font-normal text-text-tertiary">
              {subtitle}
            </div>
          ) : null}
        </div>

        <div className="justify-self-end">
          <IconButton
            onClick={onCloseClick}
            aria-label={intl.formatMessage({
              defaultMessage: "Close drawer",
              description: "Button label for closing the drawer",
            })}
            size="small"
          >
            <XIcon size={20} weight="bold" color="var(--color-text-primary)" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}

export function DrawerTopBarActionButton({
  onClick,
  children,
}: Pick<ButtonProps, "children" | "onClick">) {
  return (
    <Button
      onClick={(e) => {
        onClick?.(e);
        e.currentTarget.blur();
      }}
      variant="textFit"
      size="small"
    >
      {children}
    </Button>
  );
}
